/**
 * Shared reference layers for the Pathfinder + COP maps.
 *
 * - The layer list (metadata + default style) is fetched once and cached.
 * - A layer's GeoJSON is only fetched when a user toggles it on, then cached
 *   by (id, updated_at) so re-toggling is instant.
 * - Which layers are on and any per-layer style overrides are per-user
 *   (localStorage); the admin's style is the default.
 */
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import {
  fetchReferenceLayerGeoJson,
  listReferenceLayers,
  type ReferenceFeatureCollection,
  type ReferenceLayerDto,
  type ReferenceLayerStyle,
} from '../services/referenceLayerApi';

const PREFS_KEY = 'map.referenceLayers.prefs.v1';
const LIST_TTL_MS = 5 * 60_000;

type Prefs = {
  enabled: number[];
  overrides: Record<string, Partial<ReferenceLayerStyle>>;
};

type LayerData = {
  key: string;
  collection: ReferenceFeatureCollection | null;
  loading: boolean;
  error: string | null;
};

type State = {
  layers: ReferenceLayerDto[];
  listLoaded: boolean;
  listLoading: boolean;
  listError: string | null;
  listFetchedAt: number;
  prefs: Prefs;
  data: Record<number, LayerData>;
};

function loadPrefs(): Prefs {
  try {
    const raw = typeof window !== 'undefined' ? window.localStorage.getItem(PREFS_KEY) : null;
    if (!raw) return { enabled: [], overrides: {} };
    const parsed = JSON.parse(raw) as Partial<Prefs>;
    return {
      enabled: Array.isArray(parsed.enabled) ? parsed.enabled.filter((n) => Number.isInteger(n)) : [],
      overrides: parsed.overrides && typeof parsed.overrides === 'object' ? parsed.overrides : {},
    };
  } catch {
    return { enabled: [], overrides: {} };
  }
}

function savePrefs(prefs: Prefs): void {
  try {
    window.localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    /* storage unavailable */
  }
}

let state: State = {
  layers: [],
  listLoaded: false,
  listLoading: false,
  listError: null,
  listFetchedAt: 0,
  prefs: loadPrefs(),
  data: {},
};

const listeners = new Set<() => void>();

function emit(): void {
  listeners.forEach((l) => l());
}

function setState(patch: Partial<State>): void {
  state = { ...state, ...patch };
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function getSnapshot(): State {
  return state;
}

let listInFlight: Promise<void> | null = null;

export async function refreshReferenceLayerList(force = false): Promise<void> {
  if (!force && state.listLoaded && Date.now() - state.listFetchedAt < LIST_TTL_MS) return;
  if (listInFlight) return listInFlight;
  setState({ listLoading: true });
  listInFlight = (async () => {
    try {
      const layers = await listReferenceLayers();
      // Drop cached GeoJSON for layers that were re-uploaded (updated_at changed) or removed.
      const data: Record<number, LayerData> = {};
      for (const l of layers) {
        const prev = state.data[l.id];
        if (prev && prev.key === dataKey(l)) data[l.id] = prev;
      }
      setState({ layers, listLoaded: true, listLoading: false, listError: null, listFetchedAt: Date.now(), data });
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        (err instanceof Error ? err.message : 'Failed to load reference layers');
      setState({ listLoading: false, listError: typeof detail === 'string' ? detail : 'Failed to load reference layers' });
    } finally {
      listInFlight = null;
    }
  })();
  return listInFlight;
}

function dataKey(layer: ReferenceLayerDto): string {
  return `${layer.id}:${layer.updated_at}:${layer.feature_count}`;
}

async function ensureData(layer: ReferenceLayerDto): Promise<void> {
  const key = dataKey(layer);
  const existing = state.data[layer.id];
  if (existing && existing.key === key && (existing.collection || existing.loading)) return;
  setState({ data: { ...state.data, [layer.id]: { key, collection: null, loading: true, error: null } } });
  try {
    const collection = await fetchReferenceLayerGeoJson(layer.id);
    setState({ data: { ...state.data, [layer.id]: { key, collection, loading: false, error: null } } });
  } catch (err) {
    const detail =
      (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
      (err instanceof Error ? err.message : 'Failed to load layer');
    setState({
      data: {
        ...state.data,
        [layer.id]: { key, collection: null, loading: false, error: typeof detail === 'string' ? detail : 'Failed to load layer' },
      },
    });
  }
}

function updatePrefs(mutate: (prev: Prefs) => Prefs): void {
  const next = mutate(state.prefs);
  savePrefs(next);
  setState({ prefs: next });
}

/** Called by the admin panel after upload / edit / delete so maps pick up changes. */
export function invalidateReferenceLayers(): void {
  void refreshReferenceLayerList(true);
}

export type ReferenceLayerView = {
  layer: ReferenceLayerDto;
  enabled: boolean;
  /** Admin default merged with this user's overrides. */
  style: ReferenceLayerStyle;
  hasOverride: boolean;
  collection: ReferenceFeatureCollection | null;
  loading: boolean;
  error: string | null;
};

/**
 * @param active  When false (map unmounted / module hidden) nothing is fetched.
 */
export function useReferenceLayers(active = true) {
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const [, force] = useState(0);

  useEffect(() => {
    if (!active) return;
    void refreshReferenceLayerList();
  }, [active]);

  // Lazy-load GeoJSON for enabled layers.
  useEffect(() => {
    if (!active) return;
    for (const layer of snap.layers) {
      if (!layer.active) continue;
      if (snap.prefs.enabled.includes(layer.id)) void ensureData(layer);
    }
  }, [active, snap.layers, snap.prefs.enabled]);

  const views = useMemo<ReferenceLayerView[]>(
    () =>
      snap.layers
        .filter((l) => l.active)
        .map((layer) => {
          const override = snap.prefs.overrides[String(layer.id)];
          const style = override ? { ...layer.style, ...override } : layer.style;
          const d = snap.data[layer.id];
          const enabled = snap.prefs.enabled.includes(layer.id);
          return {
            layer,
            enabled,
            style,
            hasOverride: Boolean(override && Object.keys(override).length),
            collection: enabled && d && d.key === dataKey(layer) ? d.collection : null,
            loading: enabled ? Boolean(d?.loading) : false,
            error: enabled ? d?.error ?? null : null,
          };
        }),
    [snap.layers, snap.prefs, snap.data]
  );

  const setEnabled = useCallback((id: number, enabled: boolean) => {
    updatePrefs((prev) => {
      const has = prev.enabled.includes(id);
      if (enabled && !has) return { ...prev, enabled: [...prev.enabled, id] };
      if (!enabled && has) return { ...prev, enabled: prev.enabled.filter((x) => x !== id) };
      return prev;
    });
  }, []);

  const setAllEnabled = useCallback(
    (enabled: boolean) => {
      updatePrefs((prev) => ({ ...prev, enabled: enabled ? snap.layers.filter((l) => l.active).map((l) => l.id) : [] }));
    },
    [snap.layers]
  );

  const setStyleOverride = useCallback((id: number, patch: Partial<ReferenceLayerStyle>) => {
    updatePrefs((prev) => ({
      ...prev,
      overrides: { ...prev.overrides, [String(id)]: { ...(prev.overrides[String(id)] || {}), ...patch } },
    }));
  }, []);

  const resetStyle = useCallback((id: number) => {
    updatePrefs((prev) => {
      const next = { ...prev.overrides };
      delete next[String(id)];
      return { ...prev, overrides: next };
    });
  }, []);

  const retry = useCallback(
    (id: number) => {
      const layer = snap.layers.find((l) => l.id === id);
      if (!layer) return;
      setState({ data: { ...state.data, [id]: { key: '', collection: null, loading: false, error: null } } });
      void ensureData(layer);
      force((n) => n + 1);
    },
    [snap.layers]
  );

  return {
    layers: views,
    enabledLayers: views.filter((v) => v.enabled),
    listLoaded: snap.listLoaded,
    listLoading: snap.listLoading,
    listError: snap.listError,
    enabledCount: views.filter((v) => v.enabled).length,
    setEnabled,
    setAllEnabled,
    setStyleOverride,
    resetStyle,
    retry,
    refresh: () => refreshReferenceLayerList(true),
  };
}

export type ReferenceLayersController = ReturnType<typeof useReferenceLayers>;
